import { Component, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { HttpErrorResponse } from '@angular/common/http';
import moment from 'moment';
import { catchError, finalize, forkJoin, map, of, switchMap } from 'rxjs';
import { SharedModule } from '../../../../shared/shared.module';
import {
  NavigationItem,
  navigationItemsInvoices,
} from '../../../../models/navigation-item-model';
import { LabelService } from '../../../../services/label.service';
import { classes, pages } from '../../../../shared/labels';
import { StsExpensePayload, TipologiaCassaDescription } from '../sts-models';
import { FiscalProfileOutput } from '../../../../models/doctor-model';
import { StsExpenseService } from '../sts-expense.service';
import { buildStsExpenseFormFromApi } from '../sts-expense-api-form.mapper';
import { StsInvoiceCalculationsService } from '../sts-invoice-calculations.service';
import { buildStsInvoicePayload } from '../sts-invoice-form.mapper';
import { InvoicesService } from '../../../../services/invoices.service';
import { ErrorPanelService } from '../../../../services/error-panel.service';
import { DoctorService } from '../../../../services/doctor.service';

interface DocumentLineDisplay {
  descrizione: string;
  quantita: string;
  prezzo: string;
  iva_option: string;
  totale: string;
  isBollo: boolean;
}

@Component({
  selector: 'app-review-invoice-sts',
  standalone: true,
  imports: [SharedModule, ReactiveFormsModule, MatButtonModule],
  templateUrl: './review-invoice-sts.component.html',
  styleUrl: './review-invoice-sts.component.scss',
})
export class ReviewInvoiceStsComponent implements OnInit {
  navigationItems: NavigationItem[];
  invoiceForm!: FormGroup;
  doctorFiscalProfile!: FiscalProfileOutput;
  documentLines: DocumentLineDisplay[] = [];
  isSaving = false;
  isLoading = false;

  get isEditMode(): boolean {
    return !this.isReadyView && this.draftService.editingInvoiceId !== null;
  }

  get isReadyView(): boolean {
    return this.draftService.isReadyView;
  }

  get editingInvoiceId(): string | null {
    return this.draftService.editingInvoiceId;
  }

  constructor(
    public labelService: LabelService,
    private router: Router,
    private route: ActivatedRoute,
    private draftService: StsExpenseService,
    private fb: FormBuilder,
    private stsCalculationsService: StsInvoiceCalculationsService,
    private invoicesService: InvoicesService,
    private doctorService: DoctorService,
    private errorPanelService: ErrorPanelService
  ) {
    this.navigationItems = navigationItemsInvoices.map((item) => {
      if (item.label === pages.invoices_list) {
        return { ...item, class: classes.hnb };
      }
      return { ...item };
    });
  }

  ngOnInit(): void {
    const routeInvoiceId = this.route.snapshot.paramMap.get('invoiceId');

    if (routeInvoiceId) {
      this.loadReadyView(routeInvoiceId);
      return;
    }

    if (!this.draftService.hasDraft() || !this.draftService.invoiceForm) {
      this.router.navigate(['/fatture/sts']);
      return;
    }

    this.invoiceForm = this.draftService.invoiceForm;
    this.doctorFiscalProfile = this.draftService.doctorFiscalProfile!;
    this.updateInvoiceTotals();
    this.buildDocumentLinesDisplay();
  }

  /** Loads a ready STS expense from the list (`/fatture/sts/:id/riepilogo`). */
  private loadReadyView(invoiceId: string): void {
    if (
      this.draftService.hasDraft() &&
      this.draftService.isReadyView &&
      this.draftService.editingInvoiceId === invoiceId &&
      this.draftService.invoiceForm
    ) {
      this.invoiceForm = this.draftService.invoiceForm;
      this.doctorFiscalProfile = this.draftService.doctorFiscalProfile!;
      this.buildDocumentLinesDisplay();
      return;
    }

    this.draftService.clear();
    this.isLoading = true;

    forkJoin({
      fiscalProfile: this.doctorService.getFiscalProfile(),
      privateClients: this.invoicesService.getInvoicesPrivateClients(),
      invoice: this.invoicesService.getSTSInvoiceById(invoiceId),
    }).subscribe({
      next: ({ fiscalProfile, privateClients, invoice }) => {
        const sortedClients = [...privateClients].sort((a, b) =>
          a.client_last_name.localeCompare(b.client_last_name)
        );
        this.doctorFiscalProfile = fiscalProfile;
        this.invoiceForm = buildStsExpenseFormFromApi(
          this.fb,
          invoice,
          sortedClients
        );
        this.draftService.saveDraft(
          this.invoiceForm,
          fiscalProfile,
          sortedClients,
          0,
          invoiceId
        );
        this.draftService.viewMode = 'ready';
        this.buildDocumentLinesDisplay();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading STS expense for review:', error);
        this.errorPanelService.showErrorSnackBar(
          'Errore durante il caricamento del riepilogo della spesa STS'
        );
        this.isLoading = false;
        this.router.navigate(['/fatture']);
      },
    });
  }

  get imponibileLabel(): string {
    const regime = this.doctorFiscalProfile?.regime_fiscale;
    const escludiBollo = this.invoiceForm.get('escludi_bollo')?.value;
    if ((regime && regime !== 'RF19') || escludiBollo) {
      return 'Imponibile:';
    }
    return 'Imponibile (incluso bollo):';
  }

  get showBolloRow(): boolean {
    const regime = this.doctorFiscalProfile?.regime_fiscale;
    return !!regime && regime !== 'RF19';
  }

  get tipologiaCassaLabel(): string {
    const code = this.invoiceForm.get('tipologia_cassa')?.value;
    if (!code) {
      return '—';
    }
    return (
      TipologiaCassaDescription[
        code as keyof typeof TipologiaCassaDescription
      ] ?? code
    );
  }

  formatDate(value: unknown): string {
    if (!value) {
      return '—';
    }
    if (moment.isMoment(value)) {
      return value.format('DD/MM/YYYY');
    }
    if (value instanceof Date) {
      return moment(value).format('DD/MM/YYYY');
    }
    return String(value);
  }

  formatValue(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '—';
    }
    return String(value);
  }

  formatBoolean(value: unknown): string {
    return value ? 'Sì' : 'No';
  }

  /** Recomputes header totals for the STS review page. */
  private updateInvoiceTotals(): void {
    const documentLinesArray = this.invoiceForm.get(
      'linee_documento'
    ) as FormArray;
    this.stsCalculationsService.updateFormWithCalculatedTotals(
      documentLinesArray,
      this.invoiceForm,
      this.doctorFiscalProfile?.regime_fiscale ?? undefined
    );
  }

  /** Builds read-only rows from the document lines FormArray. */
  private buildDocumentLinesDisplay(): void {
    const documentLinesArray = this.invoiceForm.get(
      'linee_documento'
    ) as FormArray;
    this.documentLines = documentLinesArray.controls.map((control) => {
      const line = control as FormGroup;
      const raw = line.getRawValue();
      return {
        descrizione: raw.descrizione ?? '',
        quantita: raw.quantita ?? '',
        prezzo: raw.prezzo ?? '',
        iva_option: raw.iva_option ?? '',
        totale: this.stsCalculationsService.getLineTotalValue(line).toFixed(2),
        isBollo: this.stsCalculationsService.isBolloLine(line),
      };
    });
  }

  private buildPayload(): StsExpensePayload {
    this.updateInvoiceTotals();
    return buildStsInvoicePayload(this.invoiceForm);
  }

  private getApiErrorMessage(err: unknown, fallback: string): string {
    if (err instanceof HttpErrorResponse) {
      const body = err.error;
      if (typeof body === 'string' && body.trim()) {
        return body;
      }
      if (body && typeof body === 'object') {
        const message =
          (body as { message?: string }).message ??
          (body as { detail?: string }).detail ??
          (body as { error?: string }).error;
        if (message && String(message).trim()) {
          return String(message);
        }
      }
    }
    return fallback;
  }

  private navigateToListAfterSuccess(): void {
    this.draftService.clear();
    this.router.navigate(['/fatture']);
  }

  onBackToEdit(): void {
    const editingId = this.draftService.editingInvoiceId;
    if (editingId) {
      this.router.navigate(['/fatture/sts', editingId]);
      return;
    }
    this.router.navigate(['/fatture/sts']);
  }

  /** Clears the draft and returns to the invoices list (same as legacy Annulla). */
  onDelete(): void {
    this.draftService.clear();
    this.router.navigate(['/fatture']);
  }

  onClose(): void {
    this.draftService.clear();
    this.router.navigate(['/fatture']);
  }

  onRetrocedeToBozza(): void {
    const invoiceId = this.draftService.editingInvoiceId;
    if (!invoiceId || this.isSaving) {
      return;
    }

    const progressivoNumero =
      this.invoiceForm.get('progressivo_numero')?.value ?? '';
    this.isSaving = true;

    this.invoicesService
      .revertInvoiceSTSToBozza(invoiceId)
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe({
        next: () => {
          this.errorPanelService.showInfoSnackBar(
            `La spesa ${progressivoNumero} è stata retrocessa a bozza con successo.`
          );
          this.navigateToListAfterSuccess();
        },
        error: (err) => {
          console.error(err);
          this.errorPanelService.showErrorSnackBar(
            this.getApiErrorMessage(
              err,
              'Errore nella retrocessione della spesa.'
            )
          );
        },
      });
  }

  onSaveDraft(): void {
    if (this.isSaving) {
      return;
    }

    const payload = this.buildPayload();
    const editingId = this.draftService.editingInvoiceId;
    this.isSaving = true;

    const request$ = editingId
      ? this.invoicesService.modifyInvoiceSTSDraft(editingId, payload)
      : this.invoicesService.createInvoiceSTSDraft(payload);

    request$.pipe(finalize(() => (this.isSaving = false))).subscribe({
      next: () => {
        this.errorPanelService.showInfoSnackBar(
          editingId
            ? `Spesa in bozza ${payload.progressivo_numero} modificata con successo.`
            : `Spesa ${payload.progressivo_numero} salvata con successo in stato bozza.`
        );
        this.navigateToListAfterSuccess();
      },
      error: (err) => {
        console.error(err);
        this.errorPanelService.showErrorSnackBar(
          this.getApiErrorMessage(
            err,
            editingId
              ? 'Errore nella modifica della fattura.'
              : 'Errore nella creazione della fattura.'
          )
        );
      },
    });
  }

  onSaveReady(): void {
    if (this.isSaving) {
      return;
    }

    const payload = this.buildPayload();
    const editingId = this.draftService.editingInvoiceId;
    const isEdit = editingId !== null;
    this.isSaving = true;

    const draftRequest$ = editingId
      ? this.invoicesService
          .modifyInvoiceSTSDraft(editingId, payload)
          .pipe(map(() => editingId))
      : this.invoicesService
          .createInvoiceSTSDraft(payload)
          .pipe(map((res) => res.id as string));

    draftRequest$
      .pipe(
        switchMap((invoiceId) =>
          this.invoicesService.promoteDraftInvoiceSTSToReady(invoiceId).pipe(
            map(() => ({ promoted: true as const })),
            catchError(() => of({ promoted: false as const }))
          )
        ),
        finalize(() => (this.isSaving = false))
      )
      .subscribe({
        next: ({ promoted }) => {
          if (promoted) {
            this.errorPanelService.showInfoSnackBar(
              isEdit
                ? `La Spesa STS è stata modificata correttamente e salvata con stato Pronta.`
                : `La Spesa STS è stata creata correttamente e salvata con stato Pronta.`
            );
          } else {
            this.errorPanelService.showErrorSnackBar(
              isEdit
                ? `La spesa STS è stata modificata con stato Bozza perché c'è stato un errore.`
                : `La spesa STS è stata creata con stato Bozza perché c'è stato un errore.`
            );
          }
          this.navigateToListAfterSuccess();
        },
        error: (err) => {
          console.error(err);
          this.errorPanelService.showErrorSnackBar(
            this.getApiErrorMessage(
              err,
              isEdit
                ? 'Errore nella modifica della spesa STS.'
                : 'Errore nella creazione della spesa STS.'
            )
          );
        },
      });
  }
}
